/**
 * Help copy from the approved prototype (docs/prototype.html `HELP` and `GUIDE`), verbatim except:
 *  - the prototype-only "role picker" step in "Start here" is dropped;
 *  - there is no per-role list. A topic carries `go` (the menu items or hub tabs it is about) and the
 *    walkthrough shows it only when GET /me → `menu` has all of them (ADR §1.4: the menu decides, never a role);
 *  - `needs` / `absent` tell two topics about the same page apart by the shape of the menu, not by who is signed in;
 *  - the prototype's `myreq` key is the live menu key `my-requests`.
 * Generated from the prototype once; edit here from now on.
 */

export interface HelpEntry {
  t: string;
  b: string;
  l?: string[];
}

export interface GuideTopic {
  id: string;
  t: string;
  sub: string;
  steps: string[];
  tracks: string[];
  /** Menu item keys or hub tab keys this topic is about. All must be in the caller's menu. Empty = always shown. */
  go: string[];
  /** Also required in the menu. */
  needs?: string[];
  /** Must NOT be in the menu. */
  absent?: string[];
}

/** Page help by hub tab key (or menu item key when the item has no tabs). */
export const HELP: Record<string, HelpEntry> = {
  today: { t: "Today", b: "Your starting point. It shows only what needs you right now, with the most important clients first.", l: ["Open an item to see it and act on it.", "The number on “Needs you” in the top bar is the same list.", "Priority clients sit at the top, so the order is the order to work in."] },
  day: { t: "Today", b: "Your whole day on one page: what needs you, your plan, and your timesheet.", l: ["Needs you comes first. Press an item to act on it.", "The Ops associate director sets the order of your tasks. Adjust the time you will spend, then confirm the plan.", "At the end of the day, record the real minutes and submit the timesheet. You can edit it afterwards."] },
  allwork: { t: "Work", b: "Every task, client request, escalation and approval in one list, so nothing is hidden in a separate tab.", l: ["Filter by type, status, person and client.", "The person on the left of each row is responsible for it.", "Group by client, person, due date or type."] },
  book: { t: "All clients", b: "Everyone we serve, with the most important clients first. Open a client for the full picture.", l: ["The coloured dot shows how healthy the client is.", "Each card shows the SPOC, the products and what needs attention."] },
  targets: { t: "Targets", b: "What each client has been promised or expects over the quarter, and whether we are on track.", l: ["“Behind” means it needs action this week."] },
  meetings: { t: "Meetings", b: "Minutes from client meetings. Tasks that come out of a meeting land with the client’s SPOC.", l: ["Add minutes by typing, uploading Word, Excel or text, speaking, or adding a recording.", "Check the tasks it finds, change who and when, then save."] },
  emails: { t: "Emails", b: "Client emails the system has read. It proposes tasks and flags the ones that threaten an escalation.", l: ["Accept a proposal to make it a task.", "Anything waiting too long is flagged."] },
  documents: { t: "Documents", b: "Files kept against each client, so the team stops hunting through email." },
  opps: { t: "Opportunities", b: "Cross-sell and upsell ideas spotted for each client, from spotted to with Sales." },
  uover: { t: "Usage overview", b: "Every client against every product. Each box shows the share of plan used and the change on last month.", l: ["Press a box to open that client’s full usage page.", "A red ! means it is predicted to fall under the low-use line.", "Members see their own clients only."] },
  ualerts: { t: "Predictive alerts", b: "Warnings that come before a client complains: usage down on last month, or on course to fall below the low-use line in 30 days.", l: ["The most important clients are first.", "Each warning says what is happening and what to do."] },
  cusage: { t: "Client usage", b: "One client’s usage in full: this period, last month, a month-by-month chart, the last 14 days with a 30-day forecast, other numbers, and what to do.", l: ["Earlier months are estimated until real monthly reports are uploaded.", "Copy as a WhatsApp update sends a clean summary."] },
  usagerep: { t: "Usage reports", b: "Upload each product’s usage report. The client name is in the file, so one upload covers every client on that product.", l: ["Pick the product and the period, then choose the Excel or CSV file.", "Check the preview. Rows that do not match a client are skipped, not guessed.", "Use “Type in one client” only to correct a single number."] },
  engage: { t: "Engagement", b: "The monthly rhythm every client should get: WhatsApp group, meeting, training, DIY video, testimonial, fresh look, MIS with next steps and a stakeholder update.", l: ["Red means overdue, amber means due, green means on track.", "Click a cell to record it or book a date.", "“Add what is due to the task list” turns due items into tasks for the SPOCs."] },
  plan: { t: "Team plan", b: "Every open task for each person. Set what comes first and it becomes the order on their day.", l: ["Pick a person, then move tasks between Do first, Do next and Later.", "Their My day follows your order."] },
  atrisk: { t: "At risk", b: "Early warnings, before they become escalations: tasks waiting on someone, tasks running late, and clients going quiet or falling.", l: ["Start at the top. The list is ranked by client priority and urgency.", "Each line has a suggested next step and a button to message the person."] },
  briefs: { t: "Client briefs", b: "One page per client with usage, page views, engagement, what is open and ideas to lift adoption.", l: ["Open a brief before you speak about a client.", "“Copy as a WhatsApp update” gives a ready summary."] },
  tsreport: { t: "Timesheets", b: "Who filed, who was late and who has not, by day, week or month. A member who has not filed cannot open the next day.", l: ["Day, Week and Month at the top switch the view.", "Analysis shows time logged, time not logged and time on each client.", "Manager feedback records how a person could save time. Automation ideas lists repeating work that could be automated.", "“Review and edit” corrects a timesheet. “WhatsApp” writes a short message."] },
  spocs: { t: "SPOCs", b: "Each SPOC with their clients and tasks, so you can see who is carrying what." },
  daily: { t: "Daily update", b: "What the team did today, built from their timesheets and closed tasks. Nobody has to write a separate report." },
  record: { t: "Appraisal record", b: "A fair record of escalations and mistakes, kept so appraisals have the whole picture, good and bad.", l: ["Each entry says whose watch it was on and what caused it.", "The person can reply. Nothing is deleted silently."] },
  newtoday: { t: "Created today", b: "Tasks the system proposed today from emails and meetings, waiting for a SPOC to confirm." },
  workload: { t: "Workload", b: "How many tasks each person has, and how much is overdue, so work can be balanced." },
  dropped: { t: "Dropped tasks", b: "Tasks someone chose not to do, with the reason. Review them so nothing is lost quietly." },
  review: { t: "Review queue", b: "Things waiting for a decision: tasks the system was unsure about, change requests, and closures to check." },
  ideas: { t: "Ideas", b: "Anyone can suggest a better way to work. Ideas go through to live, with the result recorded." },
  checklists: { t: "Checklists", b: "The weekly and monthly routine for each client. Set up a new checklist here, then tick items off as you do them.", l: ["Pick a client at the top, then press Add a recurring task.", "The founder and the Ops associate director can set up any client. An Ops member can set up his own clients.", "Each item needs proof when it is ticked."] },
  admin: { t: "Revenue vs usage", b: "Compares what each client pays with how much they use. Clients paying a lot but using little are the renewal risk." },
  usage: { t: "Usage by client", b: "What each client uses of each product, set against what they bought and their own usual level." },
  flags: { t: "Risk flags", b: "Automatic warnings, such as silence, low use, or a threat to leave. Acknowledge a flag once you have acted." },
  settings: { t: "Settings", b: "Set up products and how their usage is counted, routines for clients, and import history." },
  capture: { t: "Capture", b: "Say or type anything: a task, a note, a client call. The system sorts it into tasks for the right person.", l: ["If it is unsure who or what, it holds it for review instead of guessing."] },
  catchup: { t: "Fill yesterday’s timesheet", b: "Yesterday’s timesheet was not filed. Today opens as soon as you fill it in." },
  deck: { t: "Client deck", b: "Slides for a client, built from what is already in Ops: delivered tasks, usage, meetings and client tasks. Revenue never appears in a client deck.", l: ["Pick the client, the period and the sections, then press Generate deck.", "Managers can make a deck for any client. A member can make one for the clients they are SPOC for.", "Check the preview, then download it as a PDF or PowerPoint."] },
  client: { t: "Client page", b: "Everything about one client: health, products and usage, meetings, emails, tasks and escalations." },
  "my-requests": { t: "My requests", b: "Ask Ops for something in your own words and see where it stands.", l: ["Add the client, what it is for, and the date you need it by.", "You get an update when Ops acknowledges, assigns or finishes it.", "When Ops says it is done, confirm it or say what is missing."] },
};

/**
 * Help for headings, stat labels, column headers and pop-up titles, found by their text (prototype `HELP_TXT`,
 * verbatim). The first pattern that matches a label's own text wins; a label nothing matches gets no (i).
 */
export const HELP_TXT: [RegExp, HelpEntry][] = [
  [/^Business at a glance$/, { t: "Business at a glance", b: "A few numbers that show how the book of clients is doing right now." }],
  [/^Not about a client$/, { t: "Not about a client", b: "Items the system could not match to a client, such as a voice note or an internal request. Review them so they reach someone." }],
  [/^Product reports$/, { t: "Product reports", b: "One line per product: when its report was last uploaded, who uploaded it and whether this month’s report is in." }],
  [/^Reports entered$/, { t: "Reports entered", b: "Every usage report that was uploaded or typed in, with who entered it and when." }],
  [/^Updates$/, { t: "Updates", b: "Changes to your requests: acknowledged, assigned, delayed, finished. A dot marks the new ones." }],
  [/^Your requests$/, { t: "Your requests", b: "Everything you asked Ops for. Open one to see the conversation, the expected date and to confirm when it is done." }],
  [/^Why this priority$/, { t: "Why this priority", b: "The reasons this client is high, medium or low priority, and any note from the founder." }],
  [/^On track$/, { t: "On track", b: "The share of engagement activities that are not overdue." }],
  [/^Overdue$/, { t: "Overdue", b: "Items that have passed their date." }],
  [/^Waiting on someone else$/, { t: "Waiting on someone else", b: "Tasks that are blocked until another person or team replies." }],
  [/^Running late$/, { t: "Running late", b: "Tasks and requests that are past their date or about to miss their response time." }],
  [/^Going quiet or falling$/, { t: "Going quiet or falling", b: "Clients nobody has spoken to for a week or more, or whose use is low or dropping." }],
  [/^Below their low-use line$/, { t: "Low-use line", b: "Clients using less than the level set for that product, usually 40% of what they bought. They are paying for little." }],
  [/^Falling vs own usual$/, { t: "Falling vs own usual", b: "Clients whose use has dropped compared with their own recent level, not compared with other clients." }],
  [/^Data late or missing$/, { t: "Data late or missing", b: "Products where the usage report has not arrived on time. No data is not the same as zero use." }],
  [/^Typical turnaround$/, { t: "Typical turnaround", b: "How long requests like yours usually take from request to done." }],
  [/^Filed on time$/, { t: "Filed on time", b: "Timesheets submitted by 6:30 pm on the day." }],
  [/^Not filed$/, { t: "Not filed", b: "Timesheets still missing. Those people cannot open the next day until they file." }],
  [/^Response clock$/, { t: "Response clock", b: "Time left to acknowledge a request. Past 24 hours it is flagged to the Ops associate director." }],
  [/^Vs own usual$/, { t: "Vs own usual", b: "This period’s use compared with the client’s own recent average. Minus 20% means they are using a fifth less than they normally do." }],
  [/^Used this period, of what they bought$/, { t: "Used of plan", b: "What the client has used so far this period, against what they bought. The bar turns amber and red as use gets low." }],
  [/^Logged \/ planned$/, { t: "Logged / planned", b: "Hours the person recorded, against the hours planned for the day." }],
  [/^Add a task$/, { t: "Add a task", b: "Use this for work that did not come from a meeting or an email. Tick several dates to make one task for each day, or choose a repeat to make it a routine." }],
  [/^Show it is done$/, { t: "Proof of closure", b: "Before a task closes, add the email, file or link that shows it was done, or tick “No attachment needed” and write one line. The Ops associate director can check it." }],
  [/^Upload usage report$/, { t: "Upload usage report", b: "Choose the product, then its Excel or CSV. One column holds the client name. The preview shows which rows matched." }],
  [/^Type in one client$/, { t: "Type in one client", b: "For adding or correcting a single client’s numbers. For a whole product report, use Upload usage report." }],
  [/^Triage$/, { t: "Triage", b: "Pick who takes the request and when it will be done, or ask for details, or decline with a reason. The requester is told straight away." }],
  [/^Running late\?$/, { t: "Running late", b: "Give the new date and the reason. The requester sees both." }],
  [/^(Review|File) .*timesheet/, { t: "Review a timesheet", b: "Correct the hours or the note. The change is marked as edited by you. If it was not filed, saving it unlocks their next day." }],
  [/^Tell Ops what you need$/, { t: "Tell Ops what you need", b: "Say it in your own words, then add the client, what it is for and the date you need it by. The “why” helps Ops put it in the right order." }],
];

/** The help entry for a label's text, or undefined (prototype `infoFor`). */
export function helpForLabel(text: string): HelpEntry | undefined {
  const s = text.trim();
  for (const [rx, h] of HELP_TXT) if (rx.test(s)) return h;
  return undefined;
}

export const GUIDE: GuideTopic[] = [
  {
    id: "start",
    t: "Start here",
    sub: "What this is and how to find your way",
    steps: [
      "Amplispot Ops is one place for everything Ops owes our clients: tasks, requests, escalations and approvals.",
      "The menu on the left (or the bar at the bottom on a phone) takes you between areas. Areas with several pages, such as Team, open a list of pages under their name. On a phone they appear as tabs across the top.",
      "The “Needs you” button in the top bar shows how many things are waiting for you. Press it to go straight to them.",
      "Small (i) icons sit beside headings. Press one for a short explanation of that part of the page.",
    ],
    tracks: ["Who is responsible for each item", "What is overdue, blocked or at risk", "What each client uses of each product"],
    go: [],
  },
  {
    id: "today",
    t: "Today",
    sub: "What needs you right now",
    steps: [
      "Open it first thing. It shows only what needs you, with the most important clients first.",
      "Press an item to open it and act: reply, assign, close or move it.",
      "Anything the system proposed from emails, voice notes or meetings appears here for you to confirm.",
      "Clear the list and you have nothing waiting.",
    ],
    tracks: ["Overdue client tasks and requests", "Escalations and approvals", "Items the system was unsure about"],
    go: ["today"],
  },
  {
    id: "myday",
    t: "My day",
    sub: "Plan the day, then record it",
    steps: [
      "Your tasks are in the order the Ops associate director set: Do first, Do next, Later.",
      "Move the start time or length of any task, then press Confirm plan.",
      "During the day, mark tasks done. Closing a task asks for proof, or a one-line note when no attachment applies.",
      "Before 6:30 pm, record the real minutes for each task and any other work, then submit the timesheet.",
      "Made a mistake? Press Edit timesheet, change it and save.",
      "A member who has not filed yesterday’s timesheet cannot open today until they do.",
    ],
    tracks: ["Plan and start times", "Minutes spent on each task", "Work outside the plan", "How the day went"],
    go: ["day"],
    needs: ['today'],
  },
  {
    id: "memtoday",
    t: "Today",
    sub: "Your whole day on one page",
    steps: [
      "Open Today first thing. It has three parts, top to bottom: Needs you, Today’s plan, and Timesheet.",
      "Needs you: things waiting on you, most important clients first. Press one to act on it: confirm, reply, assign, close or move it.",
      "Today’s plan: your tasks in the order the Ops associate director set (Do first, Do next, Later). Adjust the time for each, then press Confirm my plan.",
      "Timesheet: before 6:30 pm, record the real minutes for each task and anything else you did, then press Submit timesheet. You can edit it afterwards.",
      "The four boxes at the top jump straight to each part.",
      "If yesterday’s timesheet is not filed, you will see that page first. Fill it in and Today opens.",
    ],
    tracks: ["What needed you and what you did about it", "Plan and start times", "Minutes spent on each task", "Work outside the plan"],
    go: ["day"],
    absent: ['today'],
  },
  {
    id: "work",
    t: "Work",
    sub: "Every task and request in one list",
    steps: [
      "The list has tasks, client requests, escalations and approvals. Use the pills to show one type.",
      "Filter by status: open, overdue, blocked, waiting for proof, proposed or done.",
      "Managers can filter by person. Everyone can filter by client.",
      "The person on the left of each row is responsible. Red “Not assigned” means nobody is.",
      "Group by client, by person, by due date or by type.",
      "Press + Add to create a task.",
      "Founder and Ops associate director: the “Whose work” row at the top shows each person with their open items. Press a name to see only their work.",
    ],
    tracks: ["Owner and SPOC on every row", "Due date, slippage and pushes", "Blockers and what they wait on"],
    go: ["allwork"],
  },
  {
    id: "tasks",
    t: "Creating and closing tasks",
    sub: "Dates, repeats, proof and blockers",
    steps: [
      "+ Add creates a task. Pick the client and the SPOC is filled in for you.",
      "Tick several dates to make one task for each day, or choose a repeat for something that happens every week or month.",
      "Closing a task asks for proof: a file, a link, or a one-line note with “No attachment needed”.",
      "If you are stuck, choose “Blocked on someone” and say who. It then shows in At risk.",
      "Cannot do it by the date? “Move due date” records it, and repeated pushes show on the row.",
      "To hand a task to someone else, ask for a change. The Ops associate director decides.",
    ],
    tracks: ["Who did what and when", "Pushes and slippage", "Proof of closure"],
    go: ["allwork"],
  },
  {
    id: "clients",
    t: "Clients",
    sub: "Everyone we serve",
    steps: [
      "All clients lists every client, most important first. The dot shows health.",
      "Open a client for its products and usage, meetings, emails, tasks and escalations.",
      "Targets, Meetings, Emails, Documents, Opportunities and Engagement are listed under Clients in the menu.",
      "Members see their own clients. Managers see all of them.",
    ],
    tracks: ["Health, priority and SPOC", "Products bought and used", "Meetings, emails and documents"],
    go: ["book"],
  },
  {
    id: "engage",
    t: "Engagement",
    sub: "The monthly rhythm for every client",
    steps: [
      "Each client should get eight things on a set beat: WhatsApp group every 2 days, a monthly meeting, training, a DIY video, a testimonial, a fresh look, an MIS with next steps and a stakeholder update.",
      "The grid shows each one as on track, due or overdue.",
      "Press a cell to record that it was done, with a note, or to book a date.",
      "Recording an MIS or a stakeholder update needs a line about what to do next or what improved.",
      "“Add what is due to the task list” turns everything due into tasks for the SPOCs.",
    ],
    tracks: ["Last done and next due for each activity", "Which clients are falling behind"],
    go: ["engage"],
  },
  {
    id: "usagearea",
    t: "Usage",
    sub: "How much clients use, and what is coming",
    steps: [
      "Open Usage in the menu. Overview shows every client against every product, with the change on last month.",
      "A red ! on a box means it is predicted to fall below the low-use line in the next 30 days.",
      "Press a box to open that client’s usage page: this period, last month, six months, the last 14 days and a 30-day forecast, other numbers, and what to do.",
      "Predictive alerts lists the warnings, most important clients first. Message the SPOC from there.",
      "Earlier months are estimated from the client’s own usual level until the monthly reports are uploaded.",
    ],
    tracks: ["Share of plan used, and change on last month", "30-day forecast and low-use warnings", "Other numbers for each product"],
    go: ["uover"],
  },
  {
    id: "usage",
    t: "Usage reports",
    sub: "Getting product numbers in",
    steps: [
      "Open Usage reports. Each product shows when its report last came in.",
      "Press Upload usage report, choose the product and period, then pick the Excel or CSV.",
      "One column must be the client name. The other columns are matched by name, such as Pageviews or Leads generated.",
      "Check the preview. Rows that do not match a client are skipped and say why.",
      "Save. Health and alerts update for every matched client.",
      "To fix one client, use Type in one client.",
    ],
    tracks: ["Main number and other numbers for each product", "Who uploaded it, when, and the file"],
    go: ["usagerep"],
  },
  {
    id: "meetings",
    t: "Meetings and minutes",
    sub: "From a meeting to tasks",
    steps: [
      "Press Log a meeting and pick the client.",
      "Add the minutes: type, upload a Word, Excel or text file, speak, or add a recording.",
      "Press Find tasks in these minutes. A table with Action, Owner and Due columns works best.",
      "Check each task: who, when and the wording. Untick any that are wrong.",
      "Save. The tasks go to the client’s SPOC.",
    ],
    tracks: ["Minutes, decisions and attendees", "Tasks created from each meeting"],
    go: ["meetings"],
  },
  {
    id: "emails",
    t: "Emails",
    sub: "Client emails turned into tasks",
    steps: [
      "The system reads client emails and proposes tasks.",
      "Accept a proposal to make it a task, or dismiss it.",
      "Emails that threaten an escalation or have waited too long are flagged.",
    ],
    tracks: ["Emails waiting for a reply", "Tasks proposed from emails"],
    go: ["emails"],
  },
  {
    id: "ops-req",
    t: "Requests from Sales",
    sub: "Acknowledge, do, hand back",
    steps: [
      "New requests appear in Work. Open one and press Triage.",
      "Choose who takes it and when it will be done, or ask for more details, or decline with a reason.",
      "The requester is told as soon as you do. The 24-hour clock stops once you acknowledge.",
      "Running late? Press Running late and give a new date and a reason.",
      "When it is finished, mark it done. Sales then confirms it, or says what is missing and it comes back to you.",
    ],
    tracks: ["Acknowledged time and owner", "Expected date and delays", "Whether the requester confirmed"],
    go: ["allwork"],
  },
  {
    id: "plan",
    t: "Team plan and At risk",
    sub: "Set the order, catch problems early",
    steps: [
      "In Team plan, pick a person and drag their tasks between Do first, Do next and Later. That order becomes their day.",
      "At risk lists what will become an escalation if nobody acts: tasks waiting on someone, tasks running late, clients going quiet.",
      "Each line suggests a next step. Press Message to write to the person on WhatsApp.",
      "Client briefs gives one page per client for when you need to answer a question about it.",
    ],
    tracks: ["Priority set for each person", "Blocked and late work", "Clients going quiet or falling"],
    go: ["plan"],
  },
  {
    id: "briefs",
    t: "Client briefs",
    sub: "Know every client at a glance",
    steps: [
      "Open Client briefs under Team. Each card is a client.",
      "Press a card for usage by product, page views and other numbers, engagement status, what is open and ideas to lift adoption.",
      "Press Copy as a WhatsApp update to send a clean summary.",
      "Use it before any call, and whenever someone asks how a client is doing.",
    ],
    tracks: ["Usage and trend by product", "Engagement on track and overdue", "Open tasks, escalations and requests"],
    go: ["briefs"],
  },
  {
    id: "timesheets",
    t: "Timesheets and messages",
    sub: "See who filed, and nudge",
    steps: [
      "Timesheets shows who filed on time, who was late and who has not filed. Switch between Day, Week and Month at the top.",
      "Week and Month show a grid of hours for each person, then Analysis (time logged, time not logged, time on each client, hours against what a client pays), Manager feedback, and Automation ideas.",
      "Manager feedback: write one line on how a person could save time. They see it on their Today page.",
      "Automation ideas: tick repeating tasks, add them to the automation sheet, then download it as Excel.",
      "Press Review and edit to correct a timesheet. For someone who has not filed, press File for them.",
      "Press WhatsApp beside a person to pick pending tasks and write a short message to copy and send.",
      "Press Copy as a WhatsApp message to share the day’s summary.",
    ],
    tracks: ["Filed, late or missing", "Hours logged and planned", "Who was nudged and when"],
    go: ["tsreport"],
  },
  {
    id: "checklists",
    t: "Checklists",
    sub: "Set up and tick off routines",
    steps: [
      "Open Checklists (under Team, or Routine on your Today page).",
      "At the top, pick a client and press Add a recurring task. Choose how often: every day, week, fortnight or month.",
      "Press For many clients to add one task to several clients at once.",
      "The founder and Ops associate director can set up any client. An Ops member can set up his own clients.",
      "Each item needs proof when you tick it.",
    ],
    tracks: ["Recurring tasks for each client", "Done, missed and due today"],
    go: ["checklists"],
  },
  {
    id: "record",
    t: "Appraisal record",
    sub: "A fair record of escalations and mistakes",
    steps: [
      "Every escalation and mistake is logged with whose watch it was on and what caused it.",
      "The person can reply, so the record shows both sides.",
      "Nothing is deleted silently. Entries can be reviewed and marked.",
      "Old tasks and escalations can be imported so the record starts from the past.",
    ],
    tracks: ["Escalations, mistakes and missed deadlines", "Cause and outcome", "The person’s reply"],
    go: ["record"],
  },
  {
    id: "money",
    t: "Money",
    sub: "Revenue, usage and risk",
    steps: [
      "Revenue vs usage compares what each client pays with how much they use.",
      "Usage by client shows each product against what they bought and their own usual level.",
      "Risk flags lists warnings such as silence or low use. Acknowledge one once you have acted.",
      "Revenue is shown only to the founder and the Ops associate director.",
    ],
    tracks: ["Billing against usage", "Low and falling use", "Risk flags"],
    go: ["admin"],
  },
  {
    id: "capture",
    t: "Capture",
    sub: "Say it and it becomes a task",
    steps: [
      "Press Capture at the top. Speak or type anything: a task, a note from a call, a client request.",
      "The system turns it into tasks and picks the owner.",
      "If it is unsure who or what, it holds the item for review instead of guessing.",
    ],
    tracks: ["What was captured and from where"],
    go: ["capture"],
  },
  {
    id: "settings",
    t: "Settings",
    sub: "Products, routines, imports",
    steps: [
      "Products and usage: define each product, what it counts, the other numbers to record and where the numbers come from.",
      "Say which clients bought which product and how much they bought.",
      "Routines: add weekly or monthly tasks for many clients at once, by hand or from a file.",
      "Import history to bring in old tasks and escalations.",
    ],
    tracks: ["Products and what they count", "Plans bought by each client", "Routines for each client"],
    go: ["settings"],
  },
  {
    id: "requests",
    t: "My requests",
    sub: "Ask Ops and follow it through",
    steps: [
      "Press Tell Ops what you need and say it in your own words.",
      "Add the client or prospect, what it is for, the date you need it by, and why that date matters. Attach files if you have them.",
      "Ops acknowledges within 24 hours. You see updates when it is acknowledged, assigned, delayed or finished.",
      "Press “What do the statuses mean?” to see what each status means and what happens next.",
      "When Ops says it is done, press Yes, it is done, or Not quite yet and say what is missing.",
    ],
    tracks: ["Who has it and the expected date", "Acknowledged time", "Delays and reasons", "Your confirmation"],
    go: ["my-requests"],
  },
  {
    id: "ideas",
    t: "Ideas",
    sub: "Suggest a better way",
    steps: [
      "Press Add an idea and describe it. No forms to fill.",
      "The Ops associate director reviews it and decides.",
      "When it goes live, the result is recorded so everyone sees what changed.",
    ],
    tracks: ["Idea, who suggested it, status and result"],
    go: ["ideas"],
  },
];
